import { Eye, EyeOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';

/** Shared text field: same left edge as password rows; no browser default inset */
const inputTextClass =
  'box-border block w-full min-w-0 max-w-full appearance-none border-0 bg-transparent py-[14px] pl-0 pr-0 text-left text-[var(--color-ink-1)] placeholder:text-[var(--color-ink-4)] outline-none ring-0 focus:ring-0 rounded-none font-body text-base [-webkit-appearance:none]';

const rowClass =
  'relative w-full min-w-0 border-b border-[var(--color-border)] transition-colors duration-150 focus-within:border-[var(--color-cinder)]';

export function AuthGateShell({
  heading: _heading,
  children,
  footer,
}: {
  heading: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div
      className="flex min-h-dvh min-h-screen flex-col items-center px-6"
      style={{
        backgroundColor: 'var(--color-canvas)',
        paddingTop: 'max(12vh, 64px)',
        paddingBottom: '48px',
      }}
    >
      <div className="mx-auto w-full max-w-sm min-w-0 flex flex-col items-center">

        {/* Wordmark — Cormorant Garamond per v3.1 spec, no animation, no shimmer. */}
        <h1 className="font-display"
          style={{
            fontWeight: 400,
            fontSize: '40px',
            letterSpacing: '-0.02em',
            margin: 0,
            lineHeight: 1.1,
            color: 'var(--color-ink-1)',
            textAlign: 'center',
          }}
        >
          HELIOS
        </h1>

        {/* Tagline */}
        <p className="font-body"
          style={{
            fontWeight: 400,
            fontSize: '14px',
            color: 'var(--color-ink-3)',
            letterSpacing: '0.02em',
            marginTop: '12px',
            textAlign: 'center',
          }}
        >
          Your physique, analyzed.
        </p>

        {/* Form content */}
        <div style={{ width: '100%', marginTop: '40px' }}>{children}</div>

        {/* ToS + footer */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p className="font-body"
            style={{
              fontSize: '11px',
              color: 'var(--color-ink-3)',
              maxWidth: '280px',
              margin: '0 auto 16px',
              lineHeight: 1.6,
            }}
          >
            By continuing, you agree to our Terms and Privacy Policy
          </p>
          {footer}
        </div>
      </div>
    </div>
  );
}

export function AuthUnderlineInput({
  type = 'text',
  placeholder,
  value,
  onChange,
  autoComplete,
  enterKeyHint,
  error,
}: {
  type?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
  error?: string | null;
}) {
  // iOS/Safari defaults autoCorrect=on + autoCapitalize=sentences which is
  // wrong for emails (auto-uppercases the first letter, autocorrects domain).
  // Disable both for any input that's not free-text.
  const isStructured = type === 'email' || type === 'tel' || type === 'url';
  return (
    <div className="mb-1 w-full min-w-0">
      <div className={rowClass}>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          enterKeyHint={enterKeyHint}
          autoCorrect={isStructured ? 'off' : undefined}
          autoCapitalize={isStructured ? 'none' : undefined}
          spellCheck={isStructured ? false : undefined}
          className={`${inputTextClass} pr-10`}
        />
      </div>
      <p className="min-h-[1.125rem] pt-1 font-body text-xs text-[var(--color-status-critical)]">
        {error || '\u00a0'}
      </p>
    </div>
  );
}

export function AuthPasswordInput({
  placeholder,
  value,
  onChange,
  autoComplete,
  enterKeyHint,
  error,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
  error?: string | null;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="mb-1 w-full min-w-0">
      <div className={`${rowClass} relative`}>
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          enterKeyHint={enterKeyHint}
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className={`${inputTextClass} pr-10`}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          className="absolute bottom-3 right-0 p-1 text-[var(--color-ink-4)] transition-colors hover:text-[var(--color-ink-2)]"
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
        </button>
      </div>
      <p className="min-h-[1.125rem] pt-1 font-body text-xs text-[var(--color-status-critical)]">
        {error || '\u00a0'}
      </p>
    </div>
  );
}

/**
 * AuthGoldButton — primary auth submit button. Flat cinder pill per v3.1
 * (no gradient, no glow, no shimmer, no entrance animation).
 *
 * Scope (per Sprint B.20 design call): this is the canonical auth primitive,
 * used by Login / Signup / ResetPassword. Auth is workflow chrome, not
 * ceremony — no inner-glow treatment. For ceremony + feature moments
 * (Analysis primary action, paywall conversion, etc.) use `<ChironCTA>`
 * which intentionally adds the glow.
 *
 * Name kept for backward import compat across Login/Signup/ResetPassword;
 * "Gold" is a v2 legacy holdover (the button is cinder, not gold).
 */
export function AuthGoldButton({
  children,
  disabled,
  type = 'submit',
}: {
  children: ReactNode;
  disabled?: boolean;
  type?: 'submit' | 'button';
}) {
  return (
    <div className="mt-2 w-full">
      <button
        type={type}
        disabled={disabled}
        className="w-full rounded-full py-4 font-semibold tracking-[0.02em] transition-colors duration-150 ease-out hover:bg-[var(--color-cinder-muted)] active:bg-[var(--color-cinder-muted)] disabled:cursor-not-allowed disabled:opacity-60 font-body text-sm"
        style={{
          background: 'var(--color-cinder)',
          color: '#F2ECE0',
          border: 'none',
          minHeight: '48px',
        }}
      >
        {children}
      </button>
    </div>
  );
}
